import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useMemo, useState } from 'preact/hooks';

type Kind = 'info' | 'success' | 'error';
interface ToastItem {
  id: number;
  text: string;
  kind: Kind;
}
interface ToastApi {
  toast: (text: string, kind?: Kind) => void;
  success: (text: string) => void;
  error: (text: string) => void;
}

const Ctx = createContext<ToastApi>({ toast: () => {}, success: () => {}, error: () => {} });
let seq = 0;

export function ToastProvider({ children }: { children: ComponentChildren }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const toast = useCallback((text: string, kind: Kind = 'info') => {
    const id = ++seq;
    setItems((xs) => [...xs.slice(-2), { id, text, kind }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), kind === 'error' ? 4500 : 2800);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({ toast, success: (t) => toast(t, 'success'), error: (t) => toast(t, 'error') }),
    [toast],
  );
  return (
    <Ctx.Provider value={api}>
      {children}
      <div class="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} class={`toast ${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(Ctx);
}
